import { assert, CheckedAddress, NormalizedNumber, raise } from '@sparkdotfi/common-universal'
import { useQueries, useSuspenseQueries } from '@tanstack/react-query'
import { entries } from 'remeda'
import { useConfig } from 'wagmi'

import { SimplifiedQueryResult } from '../common/query'
import { Token } from '../types/Token'
import { TokenDefinitionRepository, TokenRepository } from './TokenRepository'
import { TokenDataResult, tokenDataQueryOptions } from './tokenDataQueryOptions'
import { PriceConfig } from './types'
import { getChainConfigEntry } from '@/config/chain'
import { TokenSymbol } from '@/domain/types/TokenSymbol'

interface UseTokenRepositoryParams {
  chainId: number
  priceConfigs: Record<TokenSymbol, PriceConfig>
  account: CheckedAddress | undefined
}

export function useTokenRepository({ chainId, priceConfigs, account }: UseTokenRepositoryParams): TokenRepository {
  const wagmiConfig = useConfig()
  const { tokenDefinitionRepository } = getChainConfigEntry(chainId)

  const results = useSuspenseQueries({
    queries: entries(priceConfigs).map(([symbol, priceConfig]) => {
      const token = tokenDefinitionRepository.findOneBySymbol(symbol)
      return tokenDataQueryOptions({ token, priceConfig, chainId, wagmiConfig, account })
    }),
  })

  const tokenDataResults = results.map((result) => result.data)

  return buildTokenRepository(tokenDataResults)
}

export function useTokenRepositoryQuery({
  chainId,
  priceConfigs,
  account,
}: UseTokenRepositoryParams): SimplifiedQueryResult<TokenRepository> {
  const wagmiConfig = useConfig()
  const { tokenDefinitionRepository } = getChainConfigEntry(chainId)

  return useQueries({
    queries: entries(priceConfigs).map(([symbol, priceConfig]) => {
      const token = tokenDefinitionRepository.findOneBySymbol(symbol)
      return tokenDataQueryOptions({ token, priceConfig, chainId, wagmiConfig, account })
    }),
    combine: (results) => {
      if (results.some((r) => r.isPending)) {
        return { isPending: true as const, isError: false as const, error: null, data: undefined }
      }

      const error = results.find((r) => r.isError)?.error
      if (error) {
        return { isPending: false as const, isError: true as const, error, data: undefined }
      }

      const tokenDataResults = results.map((r) => r.data!)
      return {
        isPending: false as const,
        isError: false as const,
        error: null,
        data: buildTokenRepository(tokenDataResults),
      }
    },
  })
}

export function createTokenRepository(
  tokenDefinitionRepository: TokenDefinitionRepository,
  pricesBySymbol: Record<TokenSymbol, NormalizedNumber>,
  balancesBySymbol?: Record<TokenSymbol, NormalizedNumber>,
): TokenRepository {
  const tokens = tokenDefinitionRepository.all()
  const tokenDataResults: TokenDataResult[] = tokens.map((token) => {
    const price = pricesBySymbol[token.symbol]
    assert(price !== undefined, `Missing price for token ${token.symbol}`)
    const balance = balancesBySymbol?.[token.symbol]
    if (balancesBySymbol) {
      assert(balance !== undefined, `Missing balance for token ${token.symbol}`)
    }
    return { token: new Token({ ...token, unitPriceUsd: price }), balance }
  })

  return buildTokenRepository(tokenDataResults)
}

function buildTokenRepository(tokenDataResults: TokenDataResult[]): TokenRepository {
  const tokensArray = tokenDataResults.map((r) => r.token)
  const balancesMap = new Map<TokenSymbol, NormalizedNumber | undefined>(
    tokenDataResults.map((r) => [r.token.symbol, r.balance]),
  )

  function findBySymbol(symbol: TokenSymbol): Token | undefined {
    return tokensArray.find((t) => t.symbol === symbol)
  }
  function findOneBySymbol(symbol: TokenSymbol): Token {
    return findBySymbol(symbol) ?? raise(`Token with symbol ${symbol} not found`)
  }
  function findOneBalance(symbol: TokenSymbol): NormalizedNumber {
    const balance = balancesMap.get(symbol)
    if (balance === undefined) {
      raise(`Balance not available for token ${symbol}`)
    }
    return balance
  }
  function findOneBalanceOrZero(symbol: TokenSymbol): NormalizedNumber {
    return balancesMap.get(symbol) ?? NormalizedNumber.ZERO
  }

  return {
    all: () => tokensArray,
    findBySymbol,
    findOneBySymbol,
    findOneBalance,
    findOneBalanceOrZero,
  }
}
